import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { getMyStudentProfile, saveMyStudentProfile } from "@/lib/api/student-portal.functions";
import { QUALIFICATIONS, SA_PROVINCES, profileCompletion } from "@/lib/student-portal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/portal/student/profile")({
  component: StudentProfilePage,
});

function StudentProfilePage() {
  const fetchProfile = useServerFn(getMyStudentProfile);
  const save = useServerFn(saveMyStudentProfile);
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["student-profile"], queryFn: () => fetchProfile() });

  const [form, setForm] = useState({
    full_name: "",
    student_number: "",
    institution: "",
    qualification: "llb",
    academic_year: "1",
    semester: "1",
    phone: "",
    province: "",
    career_interests: "",
    linkedin_url: "",
    avatar_url: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const p: any = data?.profile;
    if (!p && !data?.base) return;
    setForm((f) => ({
      ...f,
      full_name: p?.full_name ?? data?.base?.full_name ?? "",
      student_number: p?.student_number ?? "",
      institution: p?.institution ?? "",
      qualification: p?.qualification ?? "llb",
      academic_year: String(p?.academic_year ?? 1),
      semester: String(p?.semester ?? 1),
      phone: p?.phone ?? data?.base?.phone ?? "",
      province: p?.province ?? "",
      career_interests: (p?.career_interests ?? []).join(", "),
      linkedin_url: p?.linkedin_url ?? "",
      avatar_url: p?.avatar_url ?? "",
    }));
  }, [data]);

  const completion = profileCompletion({
    ...form,
    career_interests: form.career_interests ? form.career_interests.split(",") : [],
  });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await save({
        data: {
          full_name: form.full_name,
          student_number: form.student_number,
          institution: form.institution,
          qualification: form.qualification as any,
          academic_year: Number(form.academic_year) || null,
          semester: Number(form.semester) || null,
          phone: form.phone,
          province: form.province,
          linkedin_url: form.linkedin_url,
          avatar_url: form.avatar_url,
          career_interests: form.career_interests
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .slice(0, 12),
        },
      });
      toast.success("Student profile saved");
      qc.invalidateQueries({ queryKey: ["student-profile"] });
      qc.invalidateQueries({ queryKey: ["student-dashboard"] });
    } catch (err: any) {
      toast.error("Could not save", { description: err?.message });
    } finally {
      setSaving(false);
    }
  }

  const set = (k: keyof typeof form) => (e: any) => setForm({ ...form, [k]: e.target.value });

  return (
    <section className="max-w-2xl">
      <div className="mb-4 rounded-lg border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">Profile completion</p>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-navy transition-all" style={{ width: `${completion}%` }} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{completion}% complete</p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4 rounded-lg border border-border bg-card p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="sp_name">Full name</Label>
            <Input id="sp_name" value={form.full_name} onChange={set("full_name")} required />
          </div>
          <div>
            <Label htmlFor="sp_number">Student number</Label>
            <Input id="sp_number" value={form.student_number} onChange={set("student_number")} />
          </div>
          <div>
            <Label htmlFor="sp_inst">Institution</Label>
            <Input id="sp_inst" value={form.institution} onChange={set("institution")} placeholder="UNISA" />
          </div>
          <div>
            <Label htmlFor="sp_qual">Qualification</Label>
            <select
              id="sp_qual"
              value={form.qualification}
              onChange={set("qualification")}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {QUALIFICATIONS.map((q) => (
                <option key={q.value} value={q.value}>
                  {q.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="sp_year">Year of study</Label>
            <Input id="sp_year" type="number" min={1} max={8} value={form.academic_year} onChange={set("academic_year")} />
          </div>
          <div>
            <Label htmlFor="sp_sem">Semester</Label>
            <Input id="sp_sem" type="number" min={1} max={4} value={form.semester} onChange={set("semester")} />
          </div>
          <div>
            <Label htmlFor="sp_phone">Phone</Label>
            <Input id="sp_phone" value={form.phone} onChange={set("phone")} />
          </div>
          <div>
            <Label htmlFor="sp_prov">Province</Label>
            <select
              id="sp_prov"
              value={form.province}
              onChange={set("province")}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Select…</option>
              {SA_PROVINCES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <Label htmlFor="sp_interests">Career interests (comma separated)</Label>
          <Input
            id="sp_interests"
            value={form.career_interests}
            onChange={set("career_interests")}
            placeholder="Labour law, Corporate, Litigation"
          />
        </div>
        <div>
          <Label htmlFor="sp_li">LinkedIn URL</Label>
          <Input id="sp_li" value={form.linkedin_url} onChange={set("linkedin_url")} />
        </div>
        <div>
          <Label htmlFor="sp_avatar">Profile photo URL</Label>
          <Input id="sp_avatar" value={form.avatar_url} onChange={set("avatar_url")} />
        </div>
        <Button type="submit" disabled={saving} className="bg-navy hover:bg-navy/90">
          {saving ? "Saving…" : "Save profile"}
        </Button>
      </form>
    </section>
  );
}
